import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";
import { useReportsStoreRefactor } from "./reportsStoreRefactor";

/**
 * Report Search Store
 *
 * This store is specifically designed for search functionality and is separate from
 * the main reports store. It provides:
 *
 * 1. Search-only context (no caching, infinite loading support)
 * 2. Report-specific getters for search results
 * 3. Real-time updates bridge from main store
 *
 * REAL-TIME UPDATES ARCHITECTURE:
 * ================================
 *
 * Problem: Search stores don't have ActionCable subscriptions (to avoid conflicts),
 * but users expect to see new items appear in search results when they're created
 * via ActionCable in other parts of the app.
 *
 * Solution: Bridge pattern that connects main store ActionCable updates to search stores:
 *
 * 1. When search() is called, we subscribe to the main store's _upsertItem method
 * 2. We override the main store's _upsertItem to also add new items to search results
 * 3. ALL new items are added regardless of search criteria (simplified UX)
 * 4. This ensures users always see new items appear in search views
 *
 * Flow:
 * ActionCable → Main Store → _upsertItem override → Search Store (if matches criteria)
 *
 * Benefits:
 * - No ActionCable conflicts (only main stores subscribe)
 * - Search stores get real-time updates for all new items
 * - Clean separation of concerns
 * - Simplified UX - users always see new items appear
 */

// Create base store configuration for reports with infinite loading
const reportsBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/reports",
  name: "ReportSearchStoreRefactor",
  objectName: "Rapport", // Auto-generates CRUD success messages
  enableCache: false, // Reports change frequently, don't cache
  actionCableChannel: null, // Disable ActionCable for search store (main store handles real-time updates)
  infiniteLoading: true, // Enable infinite loading for report
});

export const useReportSearchStore = defineStore("reportSearch", {
  ...reportsBaseStore,

  state: () => {
    const baseState = reportsBaseStore.state();
    return {
      ...baseState,
      currentSearchParams: null, // Track current search parameters
      mainStoreUnsubscribe: null, // Clean unsubscribe function
      actionCableSyncInProgress: false, // Flag to prevent infinite loading during ActionCable updates
      enableRealtimeBridge: true, // Disable main-store syncing to preserve API search results
    };
  },

  getters: {
    ...reportsBaseStore.getters,

    // 🔒 NEW GETTER: Safe pagination check that considers ActionCable sync state
    shouldLoadMore: (state) => {
      // Don't load more during ActionCable sync to prevent infinite loading triggers
      if (state.actionCableSyncInProgress) {
        return false;
      }
      // Otherwise check if we actually have more data to load
      return state.hasMoreData && !state.loading;
    },

    // Convenience aliases for search context
    searchResults: (state) => {
      return [...state.data].sort((a, b) => {
        // First prioritize sticky items
        if (a.sticky && !b.sticky) return -1;
        if (!a.sticky && b.sticky) return 1;

        // Within each group (sticky/non-sticky), sort by date descending (newest first)
        const dateA = new Date(a.date || a.created_at);
        const dateB = new Date(b.date || b.created_at);
        return dateB - dateA;
      });
    },
    hasSearchResults: (state) => state.data.length > 0,

    // Report-specific getters
    stickySearchResults: (state) =>
      state.data.filter((report) => report.sticky === true),
  },

  actions: {
    ...reportsBaseStore.actions,

    // Enhanced search with clean reactive bridge
    async search(searchParams = {}, append = false) {
      // Store current search parameters for real-time filtering
      const newParams = searchParams.q || searchParams;
      
      // Deep comparison of params to detect changes (including array changes)
      const paramsChanged =
        JSON.stringify(this.currentSearchParams) !== JSON.stringify(newParams);
      
      // Log for debugging contact filter issues
      if (newParams.contact_ids_cache_cont_any) {
        console.log("ReportSearchStore: search called with contact filter:", {
          currentParams: this.currentSearchParams,
          newParams: newParams,
          paramsChanged: paramsChanged,
          append: append
        });
      }
      
      this.currentSearchParams = newParams;

      // Optionally subscribe to main store updates (disabled by default)
      if (this.enableRealtimeBridge && !this.mainStoreUnsubscribe) {
        this.subscribeToMainStore();
      }

      // Check if contact filter specifically changed (array comparison)
      const contactFilterChanged = (() => {
        const oldContactFilter = this.currentSearchParams?.contact_ids_cache_cont_any || [];
        const newContactFilter = newParams.contact_ids_cache_cont_any || [];
        return JSON.stringify(oldContactFilter.sort()) !== JSON.stringify(newContactFilter.sort());
      })();
      
      // If params changed and this is not an infinite append, reset local search results
      // Also force reset if contact filter specifically changed (to ensure filtering works correctly)
      if (!append && (paramsChanged || contactFilterChanged)) {
        if (contactFilterChanged) {
          console.log("ReportSearchStore: Force resetting due to contact filter change");
        } else {
          console.log("ReportSearchStore: Resetting search results due to params change");
        }
        this.data = [];
        this.currentOffset = 0;
        this.hasMoreData = true;
      } else if (!append && !paramsChanged) {
        console.log("ReportSearchStore: Params unchanged, but not appending - this might be an issue");
      }

      // Call the original search method (API call with backend filtering)
      const results = await reportsBaseStore.actions.search.call(
        this,
        { q: newParams },
        append,
      );

      // 🔧 Share search results with main store (keep main store in sync)
      if (results && Array.isArray(results)) {
        const mainStore = useReportsStoreRefactor();

        // Merge search results into main store so it's aware of them
        results.forEach((searchResult) => {
          mainStore._upsertItem(searchResult);
        });

        console.log(
          `🔄 Shared ${results.length} search results with main store`,
        );
      }

      return results;
    },

    // ✨ CLEAN: Use Pinia's built-in reactivity instead of method override
    subscribeToMainStore() {
      if (this.mainStoreUnsubscribe) return;

      const mainStore = useReportsStoreRefactor();
      const searchStore = this;

      // Use Pinia's $subscribe to watch main store data changes
      this.mainStoreUnsubscribe = mainStore.$subscribe(
        (mutation, state) => {
          // 🔄 ALWAYS sync - even with no search params (show all reports)
          // No search params means "show all" so ActionCable updates should be reflected
          searchStore.syncWithMainStore(state.data);
        },
        { detached: true }, // Continue watching even if search store is destroyed
      );
    },

    // Sync search store with main store based on current criteria
    syncWithMainStore(mainStoreData) {
      if (!Array.isArray(mainStoreData)) return;

      // If no active search params, do not alter current results during refresh
      // (prevents briefly populating with all items while filters are being applied)
      if (!this.currentSearchParams) return;

      // GUARD: Ensure search store data is properly initialized
      if (!Array.isArray(this.data)) {
        return;
      }

      // 🔒 CRITICAL FIX: Set flag to prevent infinite loading during ActionCable sync
      this.actionCableSyncInProgress = true;

      try {
        // Get all reports from main store that match our current search criteria
        const matchingReports = this.getReportsMatchingCriteria(
          mainStoreData,
          this.currentSearchParams,
        );

        // Build a map of current search results for efficient lookup
        const currentSearchMap = new Map(
          this.data.map((report) => [report.id, report]),
        );

        // Track what we should have
        const shouldHaveIds = new Set(matchingReports.map((r) => r.id));

        // Add or update reports that match criteria
        matchingReports.forEach((report) => {
          const existingReport = currentSearchMap.get(report.id);

          if (existingReport) {
            // Update existing report if data has changed
            if (JSON.stringify(existingReport) !== JSON.stringify(report)) {
              const index = this.data.findIndex((r) => r.id === report.id);
              if (index >= 0) {
                this.data[index] = { ...report };
              }
            }
          } else {
            // Add new report
            this.data.unshift({ ...report }); // Prepend for chronological order
          }
        });

        // Remove reports that no longer match criteria or don't exist in main store
        this.data = this.data.filter((searchReport) => {
          const shouldKeep = shouldHaveIds.has(searchReport.id);
          if (!shouldKeep) {
            // Report no longer matches criteria or doesn't exist in main store
          }
          return shouldKeep;
        });
      } finally {
        // 🔒 Clear the flag after sync completes
        // Use setTimeout to ensure any reactive watchers see the flag during the sync
        setTimeout(() => {
          this.actionCableSyncInProgress = false;
        }, 0);
      }
    },

    // Check if a report matches the current search criteria
    reportMatchesCriteria(report, searchParams) {
      if (!searchParams || !report) return false;

      // Check account_id filter (most important for AccountReportsRefactor)
      if (
        searchParams.account_id_eq &&
        report.account_id != searchParams.account_id_eq
      ) {
        return false;
      }

      // Check description filter
      if (searchParams.description_cont) {
        const searchTerm = searchParams.description_cont.toLowerCase();
        if (!report.description?.toLowerCase().includes(searchTerm)) {
          return false;
        }
      }

      // Check date filter
      if (searchParams.day_eq && report.date !== searchParams.day_eq) {
        return false;
      }

      // Check user filter
      if (
        searchParams.user_id_eq &&
        report.user_id != searchParams.user_id_eq
      ) {
        return false;
      }

      // Check category filter
      if (searchParams.report_category_name_in?.length > 0) {
        if (
          !searchParams.report_category_name_in.includes(report.report_category)
        ) {
          return false;
        }
      }

      // Check important filter
      if (
        searchParams.important_eq !== "" &&
        searchParams.important_eq !== null &&
        searchParams.important_eq !== undefined
      ) {
        const isImportantFilter =
          searchParams.important_eq === true ||
          searchParams.important_eq === "true";
        if (!!report.important !== isImportantFilter) {
          return false;
        }
      }

      // Trait filters using cache string semantics
      // ANY: trait_ids_cache_cont_any contains one or more cache fragments like ",12,"
      const anyFragments = Array.isArray(searchParams.trait_ids_cache_cont_any)
        ? searchParams.trait_ids_cache_cont_any.filter(
            (s) => typeof s === "string" && s.length > 0,
          )
        : [];
      if (anyFragments.length > 0) {
        const cache =
          typeof report.trait_ids_cache === "string"
            ? report.trait_ids_cache
            : "";
        const hasAny = anyFragments.some((frag) => cache.includes(frag));
        if (!hasAny) return false;
      }

      // ALL: trait_ids_cache_cont requires the combined string ",1,2,3," style
      if (
        typeof searchParams.trait_ids_cache_cont === "string" &&
        searchParams.trait_ids_cache_cont.length > 0
      ) {
        const cache =
          typeof report.trait_ids_cache === "string"
            ? report.trait_ids_cache
            : "";
        if (!cache.includes(searchParams.trait_ids_cache_cont)) return false;
      }

      // Contact filters using cache string semantics (same as traits)
      // ANY: contact_ids_cache_cont_any contains one or more cache fragments like ",22,"
      const contactFragments = Array.isArray(searchParams.contact_ids_cache_cont_any)
        ? searchParams.contact_ids_cache_cont_any.filter(
            (s) => typeof s === "string" && s.length > 0,
          )
        : [];
      if (contactFragments.length > 0) {
        const cache =
          typeof report.contact_ids_cache === "string"
            ? report.contact_ids_cache
            : "";
        const hasAny = contactFragments.some((frag) => cache.includes(frag));
        if (!hasAny) return false;
      }

      return true;
    },

    // Get all reports from main store that match criteria
    getReportsMatchingCriteria(mainStoreData, searchParams) {
      if (!Array.isArray(mainStoreData) || !searchParams) return [];

      return mainStoreData.filter((report) =>
        this.reportMatchesCriteria(report, searchParams),
      );
    },

    // Clean up bridge when clearing
    clear() {
      this.currentSearchParams = null;

      // Clean up the subscription
      if (this.mainStoreUnsubscribe) {
        this.mainStoreUnsubscribe();
        this.mainStoreUnsubscribe = null;
      }

      return reportsBaseStore.actions.clear.call(this);
    },

    // Clean up on store destruction
    $dispose() {
      if (this.mainStoreUnsubscribe) {
        this.mainStoreUnsubscribe();
        this.mainStoreUnsubscribe = null;
      }
    },
  },
});
